import { atom, useAtomValue } from 'jotai';
import { atomFamily } from 'jotai-family';
import { selectAtom } from 'jotai/utils';
import invariant from 'tiny-invariant';
import eq from 'fast-deep-equal';
import { PlanRow } from 'modules/campaign/row';
import {
  BudgetGroup,
  PlanFactor,
  PlanResolution,
  PlanResponse,
  PlanSettings,
  PlanStrategyType,
  SmartOption,
} from '../types';
import { collapsedGroupsAtom } from './tree';
import { selectedRowsAtom } from './selection';
import { ActivityType } from 'types';

export type PlanMeta = {
  id: number;
  campaign_id: number;
  type: ActivityType;
  planning: boolean;
  // BlockState.NEW, BlockState.CANCELED, BlockState.DONE,
  locked: boolean;
  date_from: string;
  date_to: string;
  plan_strategy: PlanStrategyType;
  plan_resolution: PlanResolution;
};

export type PlanStore = PlanMeta & {
  settings: PlanSettings;
  factors: Record<number, PlanFactor>;
  rows: Record<number, PlanRow>;
  groups: Record<number, BudgetGroup>;
};

function pruneInvalidKeys(
  current: Set<string>,
  rows: Record<number, PlanRow>,
  groups: Record<number, BudgetGroup>,
): Set<string> | null {
  let changed = false;
  const next = new Set<string>();

  for (const key of current) {
    const isValid = key.startsWith('row-')
      ? Number(key.slice(4)) in rows
      : key.startsWith('group-')
        ? Number(key.slice(6)) in groups
        : false;

    if (isValid) {
      next.add(key);
    } else {
      changed = true;
    }
  }

  return changed ? next : null;
}

export const __planAtom = atom<PlanStore | null>(null);

export const planAtom = atom<PlanStore>((get) => {
  const res = get(__planAtom);
  invariant(res, 'Plan snapshot is not loaded');
  return res;
});

export const planMetaAtom = selectAtom(planAtom, (plan) => ({
  id: plan.id,
  campaign_id: plan.campaign_id,
  planning: plan.planning,
  type: plan.type,
  locked: plan.locked,
  date_from: plan.date_from,
  date_to: plan.date_to,
  //
  plan_strategy: plan.plan_strategy,
  plan_resolution: plan.plan_resolution,
}));

export function usePlanMeta(): PlanMeta {
  return useAtomValue(planMetaAtom);
}

export const planSettingsAtom = atom((get) => get(planAtom).settings);
export function usePlanSettings<T extends ActivityType = ActivityType>(): PlanSettings<T> {
  return useAtomValue(planSettingsAtom) as unknown as PlanSettings<T>;
}

export const planResponseSetterAtom = atom(null, (get, set, value: PlanResponse) => {
  const { factors: planFactor, rows: planRows, groups: planGroups, ...rest } = value;
  const factors: Record<number, PlanFactor> = {};
  const rows: Record<number, PlanRow> = {};
  const groups: Record<number, BudgetGroup> = {};

  for (const f of planFactor) factors[f.id] = f;
  for (const r of planRows) rows[r.id] = r;
  for (const g of planGroups) groups[g.id] = g;

  const prunedCollapsed = pruneInvalidKeys(get(collapsedGroupsAtom), rows, groups);
  if (prunedCollapsed) set(collapsedGroupsAtom, prunedCollapsed);

  const prunedSelection = pruneInvalidKeys(get(selectedRowsAtom), rows, groups);
  if (prunedSelection) set(selectedRowsAtom, prunedSelection);

  set(__planAtom, { factors, rows, groups, ...rest });
});

export const smartOptionsAtom = selectAtom(
  planAtom,
  (plan) =>
    Object.values(plan.rows)
      .filter(({ kind }) => kind === 'SMART')
      .map(({ id, direction, application, rate, naive }, index) => ({
        index,
        id,
        direction,
        application,
        rate,
        naive,
      })),
  eq,
);
export function useSmartOptions(): SmartOption[] {
  return useAtomValue(smartOptionsAtom);
}

const planFactorsAtom = atom((get) => get(planAtom).factors);

export function usePlanFactors(): Record<number, PlanFactor> {
  return useAtomValue(planFactorsAtom);
}

export const planRowsAtoms = atom((get) => {
  return Object.values(get(planAtom).rows);
});

const hasLockedRowsAtom = atom((get) => get(planRowsAtoms).some((row) => row.state.is_locked));

export function useHasLockedRows(): boolean {
  return useAtomValue(hasLockedRowsAtom);
}

export const hasRowsAtom = atom((get) => {
  return get(planRowsAtoms).length > 0;
});

// clip ids referenced by any row's placements — such clips can't be removed
export const usedClipIdsAtom = atom((get) => {
  const used = new Set<number>();
  for (const row of Object.values(get(planAtom).rows)) {
    const placements = (row as { radio_placements?: { clip_id: number }[] }).radio_placements;
    if (!placements) continue;
    for (const { clip_id } of placements) used.add(clip_id);
  }
  return used;
});

export function useUsedClipIds(): Set<number> {
  return useAtomValue(usedClipIdsAtom);
}

export const importPropsAtom = atom((get) => {
  const plan = get(planAtom);
  const rows = Object.values(plan.rows);
  const radioClipCount = 'radio_clips' in plan.settings ? plan.settings.radio_clips.length : 0;
  return {
    hasLockedRows: get(hasLockedRowsAtom),
    hasRows: rows.length,
    hasPlanContent:
      rows.length > 0 ||
      Object.keys(plan.groups).length > 0 ||
      Object.keys(plan.factors).length > 0 ||
      radioClipCount > 0,
    isNaive: get(planMetaAtom).plan_strategy === 'NAIVE',
  };
});

export const baseRowAtom = atomFamily((id: number) => atom((get) => get(planAtom).rows[id]));
export const baseGroupAtom = atomFamily((id: number) => atom((get) => get(planAtom).groups[id]));
